import { useQuery } from "@tanstack/react-query";
import { apiGet, apiSend } from "../../../api/client";
import type {
  ErpFieldDto,
  Level,
  MappingEntryDto,
  PreviewBody,
  ProfileSummary,
  StandardFieldDto,
  StateBody,
  TransformDto,
  ValidationBody,
} from "./types";
import type { PreviewRequest } from "./preview";
import type { Family } from "../../../domain/families";

export function versionKey(profileId: string | undefined, version: string | undefined) {
  return ["profiles", profileId, "versions", version];
}

export function useEntityProfiles(entityId: string | undefined) {
  return useQuery({
    queryKey: ["entities", entityId, "profiles"],
    queryFn: async () =>
      (await apiGet<{ items: ProfileSummary[] }>(`/api/entities/${entityId}/profiles`)).items,
    enabled: Boolean(entityId),
  });
}

/** The options behind `useProfileVersion`, so a screen reading several profiles can `useQueries` them. */
export function profileVersionQuery(profileId: string | undefined, version: string | undefined) {
  return {
    queryKey: versionKey(profileId, version),
    queryFn: () => apiGet<StateBody>(`/api/profiles/${profileId}/versions/${version}`),
    enabled: Boolean(profileId && version),
  };
}

export function useProfileVersion(profileId: string | undefined, version: string | undefined) {
  return useQuery(profileVersionQuery(profileId, version));
}

export function useStandardFields(docType: string | undefined) {
  return useQuery({
    queryKey: ["standard-fields", docType],
    queryFn: async () =>
      (await apiGet<{ items: StandardFieldDto[] }>(`/api/standard-fields?docType=${docType}`)).items,
    enabled: Boolean(docType),
  });
}

export function useErpFields(entityId: string | undefined, level?: Level, q = "") {
  const query = level ? `?level=${level}${q ? `&q=${encodeURIComponent(q)}` : ""}` : "";
  return useQuery({
    queryKey: ["entities", entityId, "erp-fields", level ?? null, q],
    queryFn: async () =>
      (await apiGet<{ items: ErpFieldDto[] }>(`/api/entities/${entityId}/erp-fields${query}`)).items,
    enabled: Boolean(entityId),
  });
}

export function useTransforms() {
  return useQuery({
    queryKey: ["transforms"],
    queryFn: async () => (await apiGet<{ items: TransformDto[] }>("/api/transforms")).items,
    staleTime: Infinity,
  });
}

/** POST .../preview, always server-side: the browser runs no transform. */
export function sendPreview(
  profileId: string,
  version: string,
  body: PreviewRequest,
  signal: AbortSignal,
): Promise<PreviewBody> {
  return apiSend<PreviewBody>(
    "POST",
    `/api/profiles/${profileId}/versions/${version}/preview`,
    body,
    signal,
  );
}

export function validateVersion(profileId: string, version: string): Promise<ValidationBody> {
  return apiSend<ValidationBody>("POST", `/api/profiles/${profileId}/versions/${version}/validate`);
}

export function publishVersion(profileId: string, version: string): Promise<ProfileSummary> {
  return apiSend<ProfileSummary>("POST", `/api/profiles/${profileId}/versions/${version}/publish`);
}

/** No `fromVersionId`, so the server seeds the family's first draft from its pre-seed (HUB-F05).
    A family that already has a profile answers 409 `PROFILE_EXISTS`; callers treat that as "open it". */
export function startProfile(entityId: string, family: Family) {
  return apiSend<ProfileSummary>("POST", `/api/entities/${entityId}/profiles`, { family });
}

export function startDraftFrom(entityId: string, family: Family, fromVersionId: string) {
  return apiSend<ProfileSummary>("POST", `/api/entities/${entityId}/profiles`, { family, fromVersionId });
}

/** One row write; the server answers with the whole StateBody. */
export function saveRow(
  profileId: string,
  version: string,
  ota: string,
  entry: MappingEntryDto,
): Promise<StateBody> {
  return apiSend<StateBody>("PUT", `/api/profiles/${profileId}/versions/${version}/rows/${ota}`, entry);
}
